import React, { useEffect, useRef, useState } from 'react';
import Styles from './styles.module.css';
import SectionTitle from './../_ui/sectionTitle';
import { IoStorefrontOutline, IoSchoolOutline, IoBusinessOutline, IoSendOutline } from "react-icons/io5";

/* 画面に入ったら数字をカウントアップ＋アイコンが上から落ちてくる。
   prefers-reduced-motion では即座に最終値を表示。 */

type Stat = { icon: JSX.Element; value: number; suffix: string; unit: string; label: string };

const STATS: Stat[] = [
  { icon: <IoStorefrontOutline />, value: 3924, suffix: '＋', unit: '店舗', label: '登録事業者' },
  { icon: <IoSchoolOutline />, value: 3723, suffix: '＋', unit: '件', label: '学生登録数' },
  { icon: <IoBusinessOutline />, value: 739, suffix: '＋', unit: '社', label: '企業登録数' },
  { icon: <IoSendOutline />, value: 218, suffix: '', unit: '校', label: '送付可能学校数' },
];

const DURATION = 1600;

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return reduced;
};

const HomeNumber = () => {
  const reduced = usePrefersReducedMotion();
  const [progress, setProgress] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const start = () => {
      if (started.current) return;
      started.current = true;
      if (reduced || typeof requestAnimationFrame === 'undefined') {
        setProgress(1);
        return;
      }
      const t0 = performance.now();
      const tick = (now: number) => {
        const raw = Math.min(1, (now - t0) / DURATION);
        setProgress(raw);
        if (raw < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (typeof IntersectionObserver === 'undefined') {
      start();
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          start();
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);

  const eased = 1 - Math.pow(1 - progress, 3);
  const on = started.current;

  return (
    <div className={Styles.number} ref={ref}>
      <SectionTitle
        kicker="Numbers"
        title="数字で見る memorii works"
        lead="サロン・学生・企業の登録が、毎月増え続けています。"
      />
      <div className={`${Styles.number_row} ${on ? Styles.on : ''}`}>
        {STATS.map((s, i) => (
          <div
            key={i}
            className={Styles.number_item}
            style={{ ['--i' as string]: i } as React.CSSProperties}
          >
            <span className={Styles.number_icon}>{s.icon}</span>
            <p className={Styles.number_value}>
              {Math.round(s.value * eased).toLocaleString()}
              {s.suffix && <span className={Styles.suffix}>{s.suffix}</span>}
              <span className={Styles.unit}>{s.unit}</span>
            </p>
            <p className={Styles.number_label}>{s.label}</p>
          </div>
        ))}
      </div>
      <p className={Styles.number_note}>2026年9月時点</p>
    </div>
  );
};

export default HomeNumber;
